import React from "react";
import { useSelector } from "react-redux";
import { getSelectedHashtagId } from "selectors/hashtagFeedSelectors";
import moment from "moment";
import { cn } from "lib/utils";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";

interface HashtagRowProps {
  hashtag: {
    hashtag_id: string;
    name: string;
    search_date?: string;
  };
  onClick: () => void;
}

export const HashtagRow: React.FC<HashtagRowProps> = ({ hashtag, onClick }) => {
  const selectedHashtagId = useSelector(getSelectedHashtagId);
  const isSelected = selectedHashtagId === hashtag.hashtag_id;

  // Calculate expiry time
  const expiresIn = hashtag.search_date
    ? moment()
        .to(moment(hashtag.search_date).add(7, "days"), true)
        .replace("a ", "1")
        .replace("minutes", "min")
        .replace("minute", "min")
        .replace("days", "d")
        .replace("day", "d")
        .replace("hours", "h")
        .replace("hour", "h")
    : null;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "w-full flex items-center justify-between",
        "py-2 pr-3",
        "font-inter text-sm text-neutral-700 transition-all duration-150",
        "relative",
        isSelected
          ? "bg-blue-50 text-neutral-900 font-medium shadow-sm border-l-[3px] border-blue-500 rounded-r-lg pl-[9px]"
          : "hover:bg-white hover:text-neutral-900 hover:shadow-sm rounded-lg pl-6",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset"
      )}
    >
      {/* Nesting indicator - subtle left border for unselected state */}
      {!isSelected && (
        <div className="absolute left-3 top-0 bottom-0 w-px bg-neutral-200" />
      )}

      {/* Hashtag name */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <i
          className={cn(
            "fa-solid fa-hashtag text-xs flex-shrink-0",
            isSelected ? "text-blue-600" : "text-neutral-400"
          )}
        />
        <span className="truncate">{hashtag.name}</span>
      </div>

      {/* Expiry time */}
      {expiresIn && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                "font-inter text-xs ml-2 flex-shrink-0 cursor-help",
                isSelected ? "text-blue-600" : "text-neutral-500"
              )}
            >
              {expiresIn}
            </span>
          </TooltipTrigger>
          <TooltipContent>Expires in {expiresIn}</TooltipContent>
        </Tooltip>
      )}
    </button>
  );
};

export default HashtagRow;
